import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../config/config';

const STATUS_CONFIG = {
  ADDED:   { sevCls: 'low',      icon: '➕', label: 'Added' },
  REMOVED: { sevCls: 'critical', icon: '➖', label: 'Removed' },
  UPDATED: { sevCls: 'medium',   icon: '🔄', label: 'Updated' },
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const SbomDiff = ({ repoName, sboms }) => {
  const { t } = useTranslation();
  const [fromCommit, setFromCommit] = useState('');
  const [toCommit, setToCommit] = useState('');
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const handleCompare = async () => {
    if (!fromCommit || !toCommit) return;
    setLoading(true);
    setError('');
    setDiff(null);
    try {
      const res = await axios.get(
        `${config.BASE_URL}/sbom/${repoName}/diff?from=${fromCommit}&to=${toCommit}`
      );
      setDiff(res.data);
    } catch (err) {
      setError('Failed to compute diff: ' + (err.response?.data?.error || err.message));
    }
    setLoading(false);
  };

  const addedEntries = asArray(diff?.added);
  const removedEntries = asArray(diff?.removed);
  const updatedEntries = asArray(diff?.updated);

  const allEntries = diff
    ? [
        ...addedEntries.map(e => ({ ...e, status: 'ADDED' })),
        ...removedEntries.map(e => ({ ...e, status: 'REMOVED' })),
        ...updatedEntries.map(e => ({ ...e, status: 'UPDATED' })),
      ]
    : [];

  const filtered = statusFilter === 'ALL'
    ? allEntries
    : allEntries.filter(e => e.status === statusFilter);

  return (
    <div>
      <div className="ce-section-header" style={{ marginBottom: '1rem' }}>
        <h6 className="ce-section-title"><span className="ce-section-icon">🔀</span> Compare two SBOMs</h6>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--bs-secondary-color)', display: 'block', marginBottom: '0.25rem' }}>From (base)</label>
          <select className="ce-filter-select" style={{ width: '100%' }} value={fromCommit} onChange={e => setFromCommit(e.target.value)}>
            <option value="">Select base commit…</option>
            {(sboms || []).map(s => (
              <option key={s.CommitHash} value={s.CommitHash}>
                {s.CommitHash.substring(0, 12)}…
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--bs-secondary-color)', display: 'block', marginBottom: '0.25rem' }}>To (compare)</label>
          <select className="ce-filter-select" style={{ width: '100%' }} value={toCommit} onChange={e => setToCommit(e.target.value)}>
            <option value="">Select target commit…</option>
            {(sboms || []).filter(s => s.CommitHash !== fromCommit).map(s => (
              <option key={s.CommitHash} value={s.CommitHash}>
                {s.CommitHash.substring(0, 12)}…
              </option>
            ))}
          </select>
        </div>
        <button
          className="ce-btn ce-btn--primary"
          onClick={handleCompare}
          disabled={!fromCommit || !toCommit || loading}
        >
          {loading ? '…' : 'Compare'}
        </button>
      </div>

      {error && <div className="alert alert-danger small">{error}</div>}

      {diff && (
        <>
          {/* Summary */}
            <div className="ce-kpi-row">
              {[
                { label: 'Added',   value: diff.addedCount ?? addedEntries.length,     color: '#16a34a' },
                { label: 'Removed', value: diff.removedCount ?? removedEntries.length, color: '#dc2626' },
                { label: 'Updated', value: diff.updatedCount ?? updatedEntries.length, color: '#ca8a04' },
              ].map(card => (
                <div key={card.label} className="ce-kpi" style={{ '--kpi-color': card.color }}>
                  <div className="ce-kpi-value">{card.value}</div>
                  <div className="ce-kpi-label">{card.label}</div>
                </div>
              ))}
            </div>

          {allEntries.length === 0 ? (
            <div className="ce-blank">
              <div className="ce-blank-icon">✅</div>
              <div className="ce-blank-title">No differences found</div>
              <div className="ce-blank-sub">The two SBOMs are identical.</div>
            </div>
          ) : (
            <>
              {/* Filter */}
              <div className="ce-filter-bar" style={{ marginBottom: '0.75rem' }}>
                {['ALL', 'ADDED', 'REMOVED', 'UPDATED'].map(s => (
                  <button
                    key={s}
                    className={`ce-btn ce-btn--sm ${statusFilter === s ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                    onClick={() => setStatusFilter(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="ce-table-wrap">
                <table className="ce-table">
                  <thead>
                    <tr>
                      <th>{t('diff.columns.status')}</th>
                      <th>{t('diff.columns.package')}</th>
                      <th>{t('diff.columns.oldVersion')}</th>
                      <th>{t('diff.columns.newVersion')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((entry, i) => {
                      const cfg = STATUS_CONFIG[entry.status];
                      return (
                        <tr key={`${entry.name}-${i}`}>
                          <td>
                            <span className={`ce-sev ce-sev--${cfg.sevCls}`}>{cfg.icon} {cfg.label}</span>
                          </td>
                          <td><code className="ce-mono">{entry.name}</code></td>
                          <td>
                            {entry.oldVersion
                              ? <code className="ce-mono" style={{ color: '#dc2626' }}>{entry.oldVersion}</code>
                              : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                          </td>
                          <td>
                            {entry.newVersion
                              ? <code className="ce-mono" style={{ color: '#16a34a' }}>{entry.newVersion}</code>
                              : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SbomDiff;
